import React from "react";
import { Toggle, SelectPicker } from "rsuite";
import PropTypes from "prop-types";
import omit from "lodash/omit";
import "./tabularview.scss";
import { dateFormatter } from "../../utils/dateUtils";
import { generateDateRange } from "../../constants/ARI-utils";

export function dateToYMD(date) {
  const y = date.getFullYear();
  const m = date.getMonth() + 1; //Month from 0 to 11
  const d = date.getDate();
  return "" + y + "-" + (m <= 9 ? "0" + m : m) + "-" + (d <= 9 ? "0" + d : d);
}

export const FIELDS = [
  { key: "day", label: "Day", type: "text", width: 130 },
  { key: "date", label: "Date", type: "text", width: 125 },
  { key: "currency", label: "Currency", type: "text", width: 75 },
  {
    key: "price",
    label: "Rate (before tax and promo)",
    type: "inputDecimal",
    width: 110,
  },
  { key: "sourceTax", label: "Tax", type: "text", width: 90 },
  { key: "isOpen", label: "Availability", type: "toggle", width: 105 },
  { key: "inventory", label: "Allotment", type: "input", width: 85 },
  { key: "minLos", label: "Min LOS", type: "input", width: 80 },
  { key: "maxLos", label: "Max LOS", type: "input", width: 80 },
  { key: "minLeadTime", label: "Min LT", type: "input", width: 80 },
  { key: "maxLeadTime", label: "Max LT", type: "input", width: 80 },
];

/**
 * Calculate scaled column widths based on available container width
 * This function dynamically scales columns to fill the available space across different screen sizes
 * @param {boolean} isEditable - Whether the property/rateplan is editable
 * @param {number} containerWidth - The available container width
 * @returns {Array} Fields array with scaled widths
 */
export const getScaledFields = (isEditable, containerWidth = null) => {
  // If no container width provided, return original fields
  if (!containerWidth) {
    return FIELDS;
  }

  // Calculate available width for the table
  // Account for padding, scrollbar and exclude column
  const TABLE_PADDING_LEFT = 5; // padding-left from .manageari-table
  const SCROLLBAR_WIDTH = 15; // Reserve space for potential scrollbar
  const EXCLUDE_COLUMN_WIDTH = 90; // Width of the "Exclude" checkbox column for offline properties

  const scrollbarReservation = SCROLLBAR_WIDTH;

  // Table always occupies full width now (upload panel moved to top bar)
  const effectiveContainerWidth = containerWidth;

  // Calculate available width for data columns
  const availableWidth =
    effectiveContainerWidth -
    TABLE_PADDING_LEFT -
    scrollbarReservation -
    (isEditable ? EXCLUDE_COLUMN_WIDTH : 0);

  // Calculate total width of all fields
  const totalOriginalWidth = FIELDS.reduce(
    (sum, field) => sum + field.width,
    0
  );

  // Calculate scale factor to distribute available space
  const scaleFactor = availableWidth / totalOriginalWidth;

  // Apply minimum scale factor to prevent columns from becoming too small
  const finalScaleFactor = Math.max(scaleFactor, 0.8);

  return FIELDS.map((field) => ({
    ...field,
    width: Math.round(field.width * finalScaleFactor),
  }));
};

export const ToggleField = ({ rowData, handleToggleChange }) => {
  const isOpenLabel = rowData?.isOpen ? "Open" : "Closed";

  // Options for SelectPicker
  const availabilityOptions = [
    { label: "Open", value: true },
    { label: "Closed", value: false },
  ];

  // Handle SelectPicker change
  const handleSelectChange = (value) => {
    handleToggleChange(value, rowData);
  };

  return (
    <div className="manageari-toggle" data-testid="toggle-container">
      {rowData.isCustomRow ? (
        <SelectPicker
          data-testid="toggle-select-picker"
          size="sm"
          placeholder={" "}
          data={availabilityOptions}
          value={rowData.isOpen}
          onChange={handleSelectChange}
          cleanable={true}
          searchable={false}
        />
      ) : (
        <Toggle
          size="sm"
          role="switch"
          data-testid="toggle-input"
          checked={rowData.isOpen}
          locale={{ on: "Open", off: "Closed" }}
          onChange={(checked) => {
            handleToggleChange(checked, rowData);
          }}
        >
          {isOpenLabel}
        </Toggle>
      )}
    </div>
  );
};

export const getDefaultCurrentData = (roomtypeId) => {
  return [
    {
      roomtypeId: roomtypeId,
      date: "unless excluded",
      day: "Applies to all rows",
      price: null,
      tax: null,
      isOpen: null,
      inventory: null,
      minLos: null,
      maxLos: null,
      minLeadTime: null,
      maxLeadTime: null,
      isCustomRow: true,
    },
  ];
};

// The ARI fields a user can edit — the ones updateByProduct payloads carry.
export const ARI_EDITABLE_KEYS = [
  "price",
  "isOpen",
  "inventory",
  "minLos",
  "maxLos",
  "minLeadTime",
  "maxLeadTime",
];

export const hasAriTobeUpdate = (currentData, previousData) => {
  let hasChanged = false;
  currentData.sort((a, b) => (a.date > b.date ? 1 : -1));
  previousData.sort((a, b) => (a.date > b.date ? 1 : -1));
  if (currentData.length !== previousData.length) {
    hasChanged = true;
  } else {
    for (let i = 0; i < currentData.length; i++) {
      for (let key of ARI_EDITABLE_KEYS) {
        if (hasFieldChanged(currentData[i][key], previousData[i][key], key))
          hasChanged = true;
      }
    }
  }
  return hasChanged;
};

const hasFieldChanged = (currentField, previousField, key) => {
  if (currentField == null) return false;

  if (key === "price") {
    return (
      Number.parseFloat(currentField.toString()) !==
      Number.parseFloat(previousField)
    );
  } else {
    return currentField !== previousField;
  }
};

export const sortDataByDate = (data) => {
  return data.sort((a, b) => new Date(a.date) - new Date(b.date));
};

ToggleField.propTypes = {
  rowData: PropTypes.shape({
    roomtypeId: PropTypes.number,
    date: PropTypes.string,
    day: PropTypes.string,
    price: PropTypes.string,
    tax: PropTypes.string,
    isOpen: PropTypes.bool,
    inventory: PropTypes.number,
    minLos: PropTypes.number,
    maxLos: PropTypes.number,
    minLeadTime: PropTypes.number,
    isCustomRow: PropTypes.bool,
  }),
  handleToggleChange: PropTypes.func,
};

// ---------------------------------------------------------------------------
// Pure transform functions (lifted from TabularView.js)
// ---------------------------------------------------------------------------

/**
 * Returns an inclusive array of YYYY-MM-DD strings for every calendar day
 * between startDate and endDate.
 *
 * DST-safe date iteration: convert the picker's local Dates to calendar
 * strings, then step in UTC via the shared helper (generateDateRange in
 * ARI-utils) and format back in UTC. Local setDate() stepping drifts an
 * hour across DST transitions and drops/duplicates a day at range edges.
 */
export const getDateStringsInRange = (startDate, endDate) =>
  generateDateRange(dateToYMD(startDate), dateToYMD(endDate)).map(
    (date) => date.toISOString().split("T")[0]
  );

/**
 * Builds the update object for a single date.
 * If the date exists in the excluded roomRows selection, that row's values are
 * returned verbatim; otherwise the bulkUpdates values are used.
 */
export const buildUpdateForDate = (dateYMD, roomRows, bulkUpdates) => {
  const excludedRow = roomRows.find((row) => row.date === dateYMD);

  if (excludedRow) {
    return {
      date: excludedRow.date,
      inventory: excludedRow.inventory,
      price: excludedRow.price,
      isOpen: excludedRow.isOpen,
      minLos: excludedRow.minLos,
      maxLos: excludedRow.maxLos,
      minLeadTime: excludedRow.minLeadTime,
      maxLeadTime: excludedRow.maxLeadTime,
    };
  }

  return {
    date: dateYMD,
    inventory: bulkUpdates?.inventory,
    price: bulkUpdates?.price,
    isOpen: bulkUpdates?.isOpen,
    minLos: bulkUpdates?.minLos,
    maxLos: bulkUpdates?.maxLos,
    minLeadTime: bulkUpdates?.minLeadTime,
    maxLeadTime: bulkUpdates?.maxLeadTime,
  };
};

/**
 * Builds the list of ARI updates for a single roomtype across the full date
 * range, filtering out entries where every ARI field is null/undefined.
 */
export const processRoomtypeUpdates = (
  roomtypeId,
  currentData,
  dateRanges,
  bulkUpdates,
  rateplan
) => {
  try {
    const roomRows = currentData
      .filter((row) => row.roomtypeId === roomtypeId && !row.isCustomRow)
      .map((row) => ({
        ...row,
        price: Number.parseFloat(Number(row.price).toFixed(2)),
      }));

    const updates = dateRanges
      .map((dateYMD) => buildUpdateForDate(dateYMD, roomRows, bulkUpdates))
      .filter((update) => {
        const ARIDataWithoutDate = Object.values({ ...omit(update, ["date"]) });
        return (
          update !== null &&
          ARIDataWithoutDate.some(
            (value) => value !== null && value !== undefined
          )
        );
      });

    return {
      rateplanId: rateplan.value,
      roomtypeId,
      updates,
    };
  } catch (error) {
    return {
      rateplanId: rateplan.value,
      roomtypeId,
      updates: [],
      errorBody: error,
    };
  }
};

/**
 * Builds the full ARI payload for the API call.
 * Accepts an options object instead of closing over component scope.
 *
 * @param {object} opts
 * @param {object} opts.selectedProperty  - { propertyId }
 * @param {Array}  opts.currentData       - current table row state
 * @param {Array}  opts.dateRange         - [startDate, endDate] as Date objects
 * @param {object} opts.rateplan          - { value }
 */
export const buildPayload = ({
  selectedProperty,
  currentData,
  dateRange,
  rateplan,
}) => {
  const bulkUpdates = currentData.find((row) => row.isCustomRow);
  const payload = {
    propertyId: selectedProperty.propertyId,
    dateRange: {
      startDate: dateToYMD(dateRange[0]),
      endDate: dateToYMD(dateRange[1]),
    },
    productUpdates: [],
  };

  const roomtypeIds = Array.from(
    new Set(currentData.map((row) => row.roomtypeId).filter((id) => id !== "0"))
  );

  const dateRanges = getDateStringsInRange(dateRange[0], dateRange[1]);

  roomtypeIds.forEach((roomtypeId) => {
    const productUpdate = processRoomtypeUpdates(
      roomtypeId,
      currentData,
      dateRanges,
      bulkUpdates,
      rateplan
    );
    // Entries with no updates are invalid for the API (400) and carry no
    // information — drop them.
    if (productUpdate.updates.length > 0) {
      payload.productUpdates.push(productUpdate);
    }
  });

  return payload;
};

/**
 * Creates a blank dummy (bulk-update) row for the given roomtypeId.
 */
export const createDummyRow = (roomtypeId = 0) => ({
  roomtypeId,
  date: "unless excluded",
  day: "Applies to all rows",
  price: null,
  sourceTax: null,
  isOpen: null,
  inventory: null,
  minLos: null,
  maxLos: null,
  minLeadTime: null,
  maxLeadTime: null,
  isCustomRow: true,
});

/**
 * Returns the formatted display date for a cell.
 * Custom rows return their literal date string; regular rows are formatted.
 */
export const convertDateFormat = (inputDate, rowData) => {
  if (rowData.isCustomRow) {
    return rowData.date;
  }
  return dateFormatter(rowData.date);
};

/**
 * Returns the weekday label for a row.
 * Custom rows return their literal "day" string.
 * Regular rows compute the weekday from the date string using UTC to ensure
 * consistent results regardless of the client timezone.
 */
export const getDayFromDate = (rowData) => {
  if (rowData.isCustomRow) {
    return rowData.day;
  }
  const utcDate = new Date(`${rowData.date}T00:00:00Z`);
  return new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    timeZone: "UTC",
  }).format(utcDate);
};

/**
 * Generates an array of empty ARI rows — one per date in the range — for the
 * given roomtype.  Used when the API returns no data for the selected date range.
 *
 * @param {object} opts
 * @param {number|string} opts.roomtypeId
 * @param {Array}         opts.dateRange       - [startDate, endDate] as Date objects
 * @param {string}        opts.defaultCurrency
 */
export const generateEmptyRowsForDateRange = ({
  roomtypeId,
  dateRange,
  defaultCurrency,
}) => {
  if (!dateRange || dateRange.length < 2 || !roomtypeId) {
    return [];
  }

  const dateStrings = getDateStringsInRange(dateRange[0], dateRange[1]);
  return dateStrings.map((dateYMD) => {
    return {
      roomtypeId,
      date: dateYMD,
      day: getDayFromDate({ date: dateYMD }),
      currency: defaultCurrency || null,
      price: null,
      sourceTax: null,
      isOpen: null,
      inventory: null,
      minLos: null,
      maxLos: null,
      minLeadTime: null,
      maxLeadTime: null,
    };
  });
};

/**
 * Merges API data with any pending edits in currentData, generates empty rows
 * when there is no API data, and prepends the dummy bulk-update row for
 * editable properties.
 *
 * @param {object} opts
 * @param {Array}        opts.data              - raw API rows
 * @param {Array}        opts.currentData       - current table row state (may include edits)
 * @param {boolean}      opts.isEditable        - whether the property/rateplan is editable
 * @param {number|string} opts.selectedRoomtype
 * @param {Array}        opts.dateRange         - [startDate, endDate] as Date objects
 * @param {string}       opts.defaultCurrency
 */
export const preprocessAriData = ({
  data,
  currentData,
  isEditable,
  selectedRoomtype,
  dateRange,
  defaultCurrency,
}) => {
  let processedData = [];

  const currentDataMap = new Map(
    currentData
      .filter((row) => !row.isCustomRow)
      .map((row) => [`${row?.roomtypeId}_${row?.date}`, row])
  );

  if (!data || data.length === 0) {
    if (selectedRoomtype && dateRange && dateRange.length >= 2) {
      const emptyRows = generateEmptyRowsForDateRange({
        roomtypeId: selectedRoomtype,
        dateRange,
        defaultCurrency,
      });
      processedData = emptyRows.map((row) =>
        currentDataMap.has(`${row.roomtypeId}_${row.date}`)
          ? currentDataMap.get(`${row.roomtypeId}_${row.date}`)
          : row
      );
    } else {
      return [];
    }
  } else {
    const updatedData = data.map((row) =>
      currentDataMap.has(`${row.roomtypeId}_${row.date}`)
        ? currentDataMap.get(`${row.roomtypeId}_${row.date}`)
        : row
    );

    processedData = sortDataByDate(updatedData).map((item) => ({
      ...item,
      day: getDayFromDate(item),
      currency: defaultCurrency || item.currency,
    }));
  }

  if (isEditable) {
    const dummyRow =
      currentData.find((row) => row.isCustomRow) ||
      createDummyRow(selectedRoomtype || 0);
    return [dummyRow, ...processedData];
  }

  return processedData;
};

// ---------------------------------------------------------------------------
// Row-update helpers (lifted from renderTable in TabularView.js)
// ---------------------------------------------------------------------------

/**
 * Returns true if the given key/value pair represents a "meaningful" (non-null,
 * non-empty, non-isCustomRow) datum.
 */
export const isValueMeaningful = (key, row) => {
  return (
    key !== "isCustomRow" &&
    row[key] !== null &&
    row[key] !== undefined &&
    row[key] !== ""
  );
};

/**
 * Returns true if the row has at least one meaningful value.
 */
export const hasMeaningfulValues = (row) => {
  return Object.keys(row).some((key) => isValueMeaningful(key, row));
};

/**
 * Removes any dummy row (isCustomRow=true) that has no meaningful values.
 */
export const cleanUpDummyRow = (data) => {
  return data.filter((row) => {
    if (row.isCustomRow) {
      return hasMeaningfulValues(row);
    }
    return true;
  });
};

/**
 * Updates the existing dummy row's field, or creates a new one if
 * isEditable=true and none exists.
 *
 * NOTE: mutates the data array in place (mirrors original behaviour).
 */
export const handleDummyRowUpdate = (
  data,
  roomtypeId,
  fieldKey,
  value,
  isEditable
) => {
  const dummyRowIndex = data.findIndex((row) => row.isCustomRow);

  if (dummyRowIndex !== -1) {
    data[dummyRowIndex] = {
      ...data[dummyRowIndex],
      [fieldKey]: value,
    };
  } else if (isEditable) {
    data.push({
      roomtypeId,
      date: "unless excluded",
      day: "Applies to all rows",
      price: null,
      sourceTax: null,
      isOpen: null,
      inventory: null,
      minLos: null,
      maxLos: null,
      minLeadTime: null,
      maxLeadTime: null,
      isCustomRow: true,
      [fieldKey]: value,
    });
  }

  return data;
};

/**
 * Updates the row in data that matches roomtypeId + date.
 * If no match, looks up the original row from ariData and adds it with the
 * new field value.
 */
export const handleRegularRowUpdate = (
  data,
  roomtypeId,
  fieldKey,
  date,
  value,
  ariData
) => {
  let rowUpdated = false;

  const updatedData = data.map((item) => {
    if (item.roomtypeId === roomtypeId && item.date === date) {
      rowUpdated = true;
      return { ...item, [fieldKey]: value };
    }
    return item;
  });

  if (!rowUpdated && date) {
    const originalRow = ariData.find(
      (item) => item.roomtypeId === roomtypeId && item.date === date
    );
    if (originalRow) {
      updatedData.push({ ...originalRow, [fieldKey]: value });
    }
  }

  return updatedData;
};

/**
 * Orchestrates a field value change on a row.
 * Dispatches to handleRegularRowUpdate or handleDummyRowUpdate depending on
 * the date value, then cleans up any stale dummy row.
 *
 * @param {object} opts
 * @param {Array}        opts.prevData    - previous table row state
 * @param {number|string} opts.roomtypeId
 * @param {string}       opts.fieldKey
 * @param {string}       opts.date
 * @param {*}            opts.value
 * @param {boolean}      opts.isEditable
 * @param {Array}        opts.ariData     - original API data (used to re-add unedited rows)
 */
export const updateRowData = ({
  prevData,
  roomtypeId,
  fieldKey,
  date,
  value,
  isEditable,
  ariData,
}) => {
  let updatedData = [...prevData];

  if (date !== "unless excluded") {
    updatedData = handleRegularRowUpdate(
      updatedData,
      roomtypeId,
      fieldKey,
      date,
      value,
      ariData
    );
  }

  if (date === "unless excluded") {
    updatedData = handleDummyRowUpdate(
      updatedData,
      roomtypeId,
      fieldKey,
      value,
      isEditable
    );
  }

  updatedData = cleanUpDummyRow(updatedData);

  return updatedData;
};
